import React, { useMemo, useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import styles from './GroupedStackedBarChart.module.scss';
import { useAppSelector } from '../../../redux/hooks';
import KPIsPill from '../KPIsPill/KPIsPill';
import { useTranslation } from 'react-i18next';
import { CurrencyInputFormatter } from '../../../utils/currencyFormatter';

/* --- Types & Constants --- */
type Theme = 'light' | 'dark';
interface Segment {
  label: string;
  value: number;
  groupName?: string;
}
interface BarData {
  label: string;
  segments: Segment[];
}
interface Props {
  data: BarData[];
  showPills?: boolean;
  isShowAllPill?: boolean;
  showLegend?: boolean;
  currency?: string;
}

const CSS_VAR_NAMES: string[] = [
  '--data-vis-data-vis-1',
  '--data-vis-data-vis-2',
  '--data-vis-data-vis-3',
  '--data-vis-data-vis-4',
  '--data-vis-data-vis-5',
  '--data-vis-data-vis-6',
  '--data-vis-data-vis-7',
  '--data-vis-data-vis-8',
  '--data-vis-data-vis-9',
  '--data-vis-data-vis-10',
  '--data-vis-data-vis-11',
  '--data-vis-data-vis-12',
  '--data-vis-data-vis-13',
];

const DEFAULT_THEME_STYLE = {
  light: {
    axis: '#707070',
    grid: '#e4e4e4',
    tooltipBg: '#ffffff',
    tooltipText: '#2c2c2c',
  },
  dark: {
    axis: '#aeaeb2',
    grid: '#636366',
    tooltipBg: '#1c1c1e',
    tooltipText: '#d8d8d9',
  },
};

export default function StackedGroupedBarChart({
  data,
  showPills = true,
  isShowAllPill = true,
  showLegend = false,
  currency = 'USD',
}: Props) {
  const { t, i18n } = useTranslation();
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme) as Theme;

  const [colors, setColors] = useState<string[]>([]);

  useEffect(() => {
    const computedStyle = getComputedStyle(document.body);
    const resolvedColors = CSS_VAR_NAMES.map(
      (name) => computedStyle.getPropertyValue(name).trim() || '#000000'
    );
    setColors(resolvedColors);
  }, [currentTheme]);

  const stylesEnv = DEFAULT_THEME_STYLE[currentTheme] || DEFAULT_THEME_STYLE.light;

  const { years, groups, items, groupToItems, groupKeys } = useMemo(() => {
    const yearsList: string[] = [];
    const groupMap: Record<string, { label: string; budget: number[]; bv: number[] }> = {};
    const itemMap: Record<string, { label: string; budget: number[]; bv: number[] }> = {};
    const linkMap: Record<string, string[]> = {};

    const allGroups = new Set<string>();
    const allItems = new Set<string>();

    data.forEach((d) =>
      d.segments.forEach((s) => {
        const gKey = s.groupName || s.label;
        const iKey = s.label;
        allGroups.add(gKey);
        allItems.add(iKey);

        if (!linkMap[gKey]) linkMap[gKey] = [];
        if (!linkMap[gKey].includes(iKey)) linkMap[gKey].push(iKey);
      })
    );

    // Initialize maps with zeros
    allGroups.forEach((gKey) => {
      groupMap[gKey] = { label: gKey, budget: [], bv: [] };
    });
    allItems.forEach((iKey) => {
      itemMap[iKey] = { label: iKey, budget: [], bv: [] };
    });

    // Process pairs of Budget/BV
    for (let i = 0; i < data.length; i += 2) {
      const budgetItem = data[i];
      const bvItem = data[i + 1];
      if (!budgetItem || !bvItem) break;

      yearsList.push(budgetItem.label);

      const currentYearItemBudget: Record<string, number> = {};
      const currentYearGroupBudget: Record<string, number> = {};
      const currentYearItemBV: Record<string, number> = {};
      const currentYearGroupBV: Record<string, number> = {};

      budgetItem.segments.forEach((seg) => {
        const gKey = seg.groupName || seg.label;
        const iKey = seg.label;
        currentYearItemBudget[iKey] = seg.value;
        currentYearGroupBudget[gKey] = (currentYearGroupBudget[gKey] || 0) + seg.value;
      });

      bvItem.segments.forEach((seg) => {
        const gKey = seg.groupName || seg.label;
        const iKey = seg.label;
        currentYearItemBV[iKey] = seg.value;
        currentYearGroupBV[gKey] = (currentYearGroupBV[gKey] || 0) + seg.value;
      });

      allGroups.forEach((gKey) => {
        groupMap[gKey].budget.push(currentYearGroupBudget[gKey] || 0);
        groupMap[gKey].bv.push(currentYearGroupBV[gKey] || 0);
      });
      allItems.forEach((iKey) => {
        itemMap[iKey].budget.push(currentYearItemBudget[iKey] || 0);
        itemMap[iKey].bv.push(currentYearItemBV[iKey] || 0);
      });
    }

    const filteredGroupKeys = Object.keys(groupMap).filter((key) => {
      const { budget, bv } = groupMap[key];
      return budget.some((val) => val !== 0) || bv.some((val) => val !== 0);
    });

    return {
      years: yearsList,
      groups: groupMap,
      items: itemMap,
      groupKeys: filteredGroupKeys,
      groupToItems: linkMap,
    };
  }, [data]);

  const [selected, setSelected] = useState<string>(isShowAllPill ? 'ALL' : groupKeys[0] || 'ALL');
  const effectiveSelection = showPills ? selected : 'ALL';

  // Memoized Chart Options
  const option = useMemo(() => {
    const isAll = effectiveSelection === 'ALL';

    let activeKeys: string[];
    let dataSource: Record<string, { label: string; budget: number[]; bv: number[] }>;

    if (isAll) {
      activeKeys = groupKeys;
      dataSource = groups;
    } else {
      activeKeys = (groupToItems[effectiveSelection] || []).filter((k) => {
        const d = items[k];
        return d.budget.some((v) => v !== 0) || d.bv.some((v) => v !== 0);
      });
      dataSource = items;
    }

    const series = activeKeys.flatMap((key) => {
      const colorIndex = isAll ? groupKeys.indexOf(key) : activeKeys.indexOf(key);

      const category = dataSource[key];
      const color = colors[colorIndex % colors.length];
      const isLast = activeKeys.indexOf(key) === activeKeys.length - 1;

      return [
        {
          name: category.label,
          type: 'bar',
          stack: 'budget',
          barWidth: isAll ? 24 : 32,
          itemStyle: { color, borderRadius: [4, 4, 0, 0] },
          label: {
            show: isLast,
            position: 'top',
            formatter: '$',
            color: stylesEnv.axis,
          },
          data: category.budget,
        },
        {
          name: category.label,
          type: 'bar',
          stack: 'bv',
          barWidth: isAll ? 24 : 32,
          itemStyle: {
            color: 'transparent',
            borderColor: color,
            borderWidth: 1,
            borderRadius: [4, 4, 0, 0],
          },
          label: {
            show: isLast,
            position: 'top',
            formatter: 'BV',
            color: stylesEnv.axis,
          },
          data: category.bv,
        },
      ];
    });

    return {
      tooltip: {
        trigger: 'item',
        backgroundColor: stylesEnv.tooltipBg,
        textStyle: { color: stylesEnv.tooltipText, fontSize: 13 },
        formatter: (params: any) => {
          const isBV = params.seriesIndex % 2 === 1;

          if (isBV) {
            return `${params.seriesName}<br/>${params.value.toLocaleString()} BV`;
          }
          return `${params.seriesName}<br/>${CurrencyInputFormatter(currency, params.value)}`;
        },
      },
      legend: showLegend
        ? {
            bottom: 0,
            left: 0,
            icon: 'circle',
            textStyle: { color: stylesEnv.axis },
            data: activeKeys.map((k) => dataSource[k].label),
          }
        : undefined,
      grid: { top: 40, left: 10, right: 10, bottom: 40, containLabel: true },
      xAxis: {
        type: 'category',
        data: years,
        axisLabel: { color: stylesEnv.axis },
        axisLine: { lineStyle: { color: stylesEnv.grid } },
      },
      yAxis: {
        type: 'value',
        splitLine: { lineStyle: { color: stylesEnv.grid } },
        axisLabel: { color: stylesEnv.axis },
      },
      series,
    };
  }, [
    groups,
    items,
    groupKeys,
    groupToItems,
    colors,
    stylesEnv,
    effectiveSelection,
    years,
    showLegend,
    currency,
  ]);

  return (
    <div className={styles.container}>
      {showPills && (
        <div className={styles.pillsOuterContainer}>
          {isShowAllPill && (
            <KPIsPill
              code="ALL"
              label="All"
              onPillClick={setSelected}
              isSelected={selected === 'ALL'}
            />
          )}
          {groupKeys.map((key, idx) => {
            const label = groups[key].label.toLowerCase();

            const translated = i18n.exists(`categoryGroups.${label}`)
              ? t(`categoryGroups.${label}`)
              : groups[key].label;
            return (
              <KPIsPill
                key={key}
                code={key}
                label={translated}
                onPillClick={setSelected}
                isSelected={selected === key}
                dotColor={colors[idx % colors.length]}
              />
            );
          })}
        </div>
      )}
      <ReactECharts option={option} notMerge={true} lazyUpdate={true} style={{ height: 420 }} />
    </div>
  );
}
